import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Eye } from 'lucide-react';
import CategoryRating from './CategoryRating';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

const CreatorCard = ({ creator, index = 0 }) => {
  const { user, login } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);

  const {
    creator_id,
    slug,
    name,
    bio,
    profile_image,
    views = 0,
    affiliate_link
  } = creator;

  // Use slug for URL, fallback to creator_id
  const creatorUrl = slug || creator_id;

  return (
    <>
      <div 
        className={`creator-card bg-white border border-slate-200 rounded-xl md:rounded-2xl overflow-hidden flex flex-col hover:border-[#FF5A00] shadow-sm hover:shadow-md opacity-0 animate-fade-in-up`}
        style={{ animationDelay: `${index * 0.05}s` }}
        data-testid={`creator-card-${creator_id}`}
      >
        {/* Profile Image */}
        <Link to={`/${creatorUrl}`} className="block">
          <div className="relative aspect-[4/5] overflow-hidden bg-slate-100">
            <img
              src={profile_image || '/favicon.svg'}
              alt={name}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              loading="lazy"
            />
            {/* Views badge */}
            <div className="absolute top-2 right-2 md:top-3 md:right-3 bg-black/60 backdrop-blur-sm text-white text-[10px] md:text-xs px-2 py-0.5 md:px-2.5 md:py-1 rounded-full flex items-center gap-1">
              <Eye className="h-2.5 w-2.5 md:h-3 md:w-3" />
              {views >= 1000 ? `${(views/1000).toFixed(1)}k` : views}
            </div>
          </div>
        </Link>

        {/* Content */}
        <div className="p-2.5 md:p-4 flex flex-col flex-1">
          <Link to={`/${creatorUrl}`}>
            <h3 className="font-outfit font-bold text-sm md:text-lg text-slate-900 hover:text-[#FF5A00] transition-colors line-clamp-1">
              {name}
            </h3>
          </Link>
          
          <div className="text-xs md:text-sm text-slate-600 mt-0.5 md:mt-1 flex-1">
            <p className={bioExpanded ? '' : 'line-clamp-3'}>
              {bio}
            </p>
            {bio && bio.length > 100 && (
              <button
                onClick={() => setBioExpanded(!bioExpanded)}
                className="text-[#FF5A00] font-medium text-xs mt-0.5 hover:underline"
                data-testid={`bio-toggle-${creator_id}`}
              >
                {bioExpanded ? 'ver menos' : 'ler mais...'}
              </button>
            )}
          </div>

          {/* Rating Score */}
          <div className="mt-1.5 md:mt-3">
            <CategoryRating compact ratings={creator} />
            <Link to={`/${creatorUrl}`} className="text-[10px] md:text-xs text-[#FF5A00] mt-0.5 md:mt-1 font-medium hover:underline block">
              Avaliar
            </Link>
          </div>

          {/* CTA Button */}
          <a
            href={affiliate_link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 md:mt-4 w-full bg-[#FF5A00] text-white font-semibold py-2 md:py-3 px-3 md:px-4 rounded-full hover:bg-[#E04F00] transition-all flex items-center justify-center gap-1.5 md:gap-2 cta-button text-xs md:text-base"
            data-testid={`creator-card-cta-${creator_id}`}
          >
            <span className="md:hidden">Ver Perfil</span>
            <span className="hidden md:inline">Ver Conteúdo Completo</span>
            <ExternalLink className="h-3 w-3 md:h-4 md:w-4" />
          </a>
        </div>
      </div>

      {/* Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Entre para avaliar</DialogTitle>
            <DialogDescription className="text-slate-600">
              Faça login para avaliar <span className="font-semibold text-slate-900">{name}</span> e ajudar a comunidade a encontrar os melhores criadores.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 mt-4">
            <button
              onClick={() => {
                setShowLoginModal(false);
                login();
              }}
              className="w-full bg-[#FF5A00] text-white font-semibold py-3 px-4 rounded-full hover:bg-[#E04F00] transition-all flex items-center justify-center gap-3 cta-button"
              data-testid="modal-login-button"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Entrar com Google
            </button>
            <button
              onClick={() => setShowLoginModal(false)}
              className="text-slate-500 hover:text-slate-700 text-sm font-medium"
            >
              Talvez depois
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreatorCard;
